import { Autocomplete, CircularProgress } from '@mui/material';
import { debounce } from '@mui/material/utils';
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { StyledTextField } from './Forms/StyledTextField';
import useRequest from '../Hooks/useRequest';
import useEkko from '../Providers/EkkoProvider';

const unhighlightStyle: CSSProperties = {};
const highlightStyle: CSSProperties = {
  ...unhighlightStyle,
  backgroundColor: '#BBB',
};
interface Props {
  disabled?: boolean;
  forwardedRef?: React.ForwardedRef<HTMLDivElement>;
  freeText?: boolean;
  label?: string;
  onChange: (coordinate: string) => void;
  trailingDelimiter: boolean | 'both';
  trimPrefix?: string;
  value?: string;
}

const Component = ({
  disabled,
  forwardedRef,
  freeText,
  label,
  trimPrefix,
  trailingDelimiter,
  onChange,
  value,
}: Props): React.JSX.Element => {
  const { api } = useEkko();

  const [isLoading, startLoading] = useRequest<string[]>();
  const [coordinates, setCoordinates] = useState<string[]>([]);
  const [lastInput, setLastInput] = useState<string>();

  const isSuffix = useMemo(() => trimPrefix !== undefined && trimPrefix !== '', [trimPrefix]);

  /** prefix removed from fetched coordinates */
  const trimmedCoordinates = useMemo(() => {
    if (!isSuffix) {
      return coordinates;
    }

    let coords: string[] = [];
    coords = coordinates.reduce((carry, c) => {
      const trimmedCoord = c.startsWith(trimPrefix!) ? c.replace(trimPrefix!, '') : c;
      if (trimmedCoord.length > 0) {
        carry.push(trimmedCoord);
      }

      return carry;
    }, [] as string[]);

    // remove dupes
    return [...new Set(coords)].sort();
  }, [coordinates, trimPrefix, isSuffix]);

  const fetchCoordinates = useCallback(
    (input?: string) => {
      const queryText = input || isSuffix ? trimPrefix + (input ?? '') : undefined;

      startLoading(
        async () => await api.getAccessibleCoordinates(queryText, trailingDelimiter),
        (result) => {
          setCoordinates(result ?? []);
        },
      );
    },
    [trimPrefix, trailingDelimiter, isSuffix],
  );

  const debouncedFetch = useCallback((input: string) => debounce(() => fetchCoordinates(input)), [fetchCoordinates]);

  /** Fetch on Props Change / initial mount */
  useEffect(() => {
    fetchCoordinates(lastInput);
  }, [fetchCoordinates]);

  const handleChange = useCallback(
    (coordinate: string) => {
      onChange(coordinate);
      setLastInput(coordinate);
      debouncedFetch(coordinate);
    },
    [trimPrefix, onChange, debouncedFetch],
  );

  return (
    <Autocomplete
      id={`coordinate-input-${label}`}
      ref={forwardedRef}
      disabled={disabled === true}
      freeSolo={freeText !== false}
      options={trimmedCoordinates}
      loading={isLoading}
      inputValue={value}
      autoHighlight
      onChange={(_, value) => handleChange(value ?? '')}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          type="text"
          label={label ?? 'Coordinate'}
          onChange={(e) => handleChange(e.target.value)}
          inputProps={{ ...params.inputProps }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option, { inputValue }) => (
        <li {...props}>
          <div>
            <Highlighter
              searchWords={[inputValue]}
              textToHighlight={option}
              highlightStyle={highlightStyle}
              unhighlightStyle={unhighlightStyle}
            />
          </div>
        </li>
      )}
    />
  );
};

const ComponentRef = React.forwardRef<HTMLDivElement, Props>((props, ref) => (
  <Component {...props} forwardedRef={ref} />
));
ComponentRef.displayName = 'ekko-coordinate-input';
export const CoordinateInput = ComponentRef;
